/**
 * Shared option lists for the runtime-strategy controls.
 *
 * The Train page Advanced section only exposes Prediction strategy
 * (defaults to "automatic" so the trainer's hyperparameter search picks
 * the best value).
 *
 * The Configure Deployment modal exposes both Long-input processing and
 * Prediction strategy, but without an "automatic" option — the trained
 * model already carries the chosen strategy, so the operator is
 * overriding it.
 *
 * Keeping the lists in one module is what guarantees the labels,
 * tooltips, and value strings stay in sync across surfaces.
 */

export const PREDICTION_AUTOMATIC = 'automatic';

export const TRUNCATION_PREDICTION_STRATEGIES = Object.freeze([
  'truncate_start',
  'truncate_end',
]);

const PREDICTION_AUTOMATIC_OPTION = Object.freeze({
  value: PREDICTION_AUTOMATIC,
  title: 'Automatic',
  description: 'Let training pick the best strategy.',
});

const LONG_INPUT_PARALLEL_OPTION = Object.freeze({
  value: 'parallel',
  title: 'Parallel',
  description: 'Process all chunks at the same time for faster completion.',
});

const LONG_INPUT_SERIAL_OPTION = Object.freeze({
  value: 'serial',
  title: 'Serial',
  description: 'Process chunks one at a time for more stable resource usage.',
});

const PREDICTION_MEAN_OPTION = Object.freeze({
  value: 'mean',
  title: 'Average confidence',
  description: 'Use the average confidence score across all chunks.',
});

const PREDICTION_MAX_OPTION = Object.freeze({
  value: 'max',
  title: 'Max confidence',
  description: 'Use the highest confidence score from all chunks.',
});

const PREDICTION_TRUNCATE_START_OPTION = Object.freeze({
  value: 'truncate_start',
  title: 'Truncate start',
  description: 'Drop tokens from the start of the input so it fits in a single chunk.',
});

const PREDICTION_TRUNCATE_END_OPTION = Object.freeze({
  value: 'truncate_end',
  title: 'Truncate end',
  description: 'Drop tokens from the end of the input so it fits in a single chunk.',
});

export const LONG_INPUT_OPTIONS_NO_AUTO = Object.freeze([
  LONG_INPUT_PARALLEL_OPTION,
  LONG_INPUT_SERIAL_OPTION,
]);

export const PREDICTION_OPTIONS_NO_AUTO = Object.freeze([
  PREDICTION_MEAN_OPTION,
  PREDICTION_MAX_OPTION,
  PREDICTION_TRUNCATE_START_OPTION,
  PREDICTION_TRUNCATE_END_OPTION,
]);

export const PREDICTION_OPTIONS_WITH_AUTO = Object.freeze([
  PREDICTION_AUTOMATIC_OPTION,
  ...PREDICTION_OPTIONS_NO_AUTO,
]);

/**
 * Truncation strategies process the input as a single chunk, so the
 * Long-input-processing control on the deploy modal must be forced to
 * ``serial`` when one of them is selected.
 */
export function predictionStrategyDisablesParallel(value) {
  return TRUNCATION_PREDICTION_STRATEGIES.includes(value);
}

/**
 * Convert the Train page state to the create_distillation_event payload.
 * ``automatic`` collapses to ``null`` because backend / persistence
 * treat absence as "no override".
 */
export function normalizeForApi({ prediction_strategy } = {}) {
  return {
    prediction_strategy:
      !prediction_strategy || prediction_strategy === PREDICTION_AUTOMATIC
        ? null
        : prediction_strategy,
  };
}

/** Human-readable label for deploy-modal / warning copy. */
export function getPredictionStrategyLabel(value) {
  const option = PREDICTION_OPTIONS_NO_AUTO.find((opt) => opt.value === value);
  return option?.title || value || 'Unknown';
}
